import React, { useState, useRef, useEffect, useMemo } from 'react';
import { apiConfig } from '../../api/ApiConfig';

// ==========================================
// 1. WebGL Support Detector & MolStar Loader
// ==========================================
const checkWebGLSupport = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
};

let molstarLoadPromise = null;

const loadMolstarAssets = () => {
  if (window.PDBeMolstarPlugin) {
    return Promise.resolve();
  }
  if (molstarLoadPromise) {
    return molstarLoadPromise;
  }

  molstarLoadPromise = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('MolStar asset loading timed out (5s limit)'));
    }, 5000);

    // Inject CSS stylesheet
    if (!document.getElementById('pdbe-molstar-css')) {
      const link = document.createElement('link');
      link.id = 'pdbe-molstar-css';
      link.rel = 'stylesheet';
      link.href = `${process.env.PUBLIC_URL || ''}/css/pdbe-molstar.css`;
      document.head.appendChild(link);
    }

    // Inject JS plugin bundle
    const script = document.createElement('script');
    script.id = 'pdbe-molstar-js';
    script.src = `${process.env.PUBLIC_URL || ''}/js/pdbe-molstar-plugin.js`;
    script.onload = () => {
      clearTimeout(timeout);
      if (window.PDBeMolstarPlugin) {
        resolve();
      } else {
        reject(new Error('PDBeMolstarPlugin object not attached to window after script load'));
      }
    };
    script.onerror = (err) => {
      clearTimeout(timeout);
      reject(new Error('Failed to load MolStar viewer script bundle: ' + err));
    };
    document.body.appendChild(script);
  });

  return molstarLoadPromise;
};

// ==========================================
// 2. High-Fidelity 2D Interactive Fallback
// ==========================================
const Molecule2DViewer = ({ data, fileName }) => {
  const canvasRef = useRef(null);
  const [rotation, setRotation] = useState({ x: -0.5, y: 0.6 });
  const [zoom, setZoom] = useState(1.0);
  const [spin, setSpin] = useState(true);
  const isDragging = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });

  const safeFileName = (fileName || 'structure.pdb').toLowerCase();

  // Parse atoms from structural text
  const atoms = useMemo(() => {
    if (!data) return [];
    
    const lines = data.split('\n');
    const parsedAtoms = [];
    
    const isMolOrSdf = safeFileName.endsWith('.mol') || 
                       safeFileName.endsWith('.sdf') || 
                       safeFileName.endsWith('.mol2') ||
                       data.includes('$$$$') || 
                       data.includes('@<TRIPOS>MOLECULE');

    if (isMolOrSdf) {
      let atomCount = 0;
      let countsLineIndex = -1;
      
      for (let i = 3; i < Math.min(10, lines.length); i++) {
        const line = lines[i];
        if (line && line.trim().match(/^\d+\s+\d+/)) {
          countsLineIndex = i;
          break;
        }
      }
      
      if (countsLineIndex !== -1) {
        const parts = lines[countsLineIndex].trim().split(/\s+/);
        atomCount = parseInt(parts[0]) || 0;
        
        for (let i = 0; i < atomCount; i++) {
          const line = lines[countsLineIndex + 1 + i];
          if (line) {
            const x = parseFloat(line.substring(0, 10).trim());
            const y = parseFloat(line.substring(10, 20).trim());
            const z = parseFloat(line.substring(20, 30).trim());
            const element = line.substring(31, 34).trim() || 'C';
            if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
              parsedAtoms.push({ x, y, z, element, label: `${element} #${i + 1}` });
            }
          }
        }
      }
    } else {
      let atomIndex = 0;
      lines.forEach(line => {
        if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
          const atomName = line.substring(12, 16).trim();
          const isCa = atomName === 'CA';
          const isHet = line.startsWith('HETATM');
          const isNucleic = ['P', "C4'", "C1'", 'N1', 'N9'].includes(atomName);
          
          if (isCa || isNucleic || isHet || atomIndex % 4 === 0) {
            const x = parseFloat(line.substring(30, 38));
            const y = parseFloat(line.substring(38, 46));
            const z = parseFloat(line.substring(46, 54));
            const resName = line.substring(17, 20).trim();
            const resNo = parseInt(line.substring(22, 26).trim()) || 0;
            const chainName = line.substring(21, 22).trim() || 'A';
            const element = line.substring(76, 78).trim() || atomName.substring(0, 1);
            
            if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
              parsedAtoms.push({
                x, y, z,
                resName, resNo, chainName, element, isCa, isNucleic, isHet,
                label: `${resName} ${resNo} (${atomName} / Chain ${chainName})`
              });
            }
          }
          atomIndex++;
        }
      });
    }
    
    return parsedAtoms;
  }, [data, safeFileName]);

  // Auto spin loop
  useEffect(() => {
    if (!spin) return;
    let animId;
    const tick = () => {
      setRotation(prev => ({ x: prev.x, y: prev.y + 0.006 }));
      animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [spin]);

  // Render 2D Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || atoms.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;
    
    const cosX = Math.cos(rotation.x);
    const sinX = Math.sin(rotation.x);
    const cosY = Math.cos(rotation.y);
    const sinY = Math.sin(rotation.y);
    
    const projected = atoms.map(atom => {
      let x1 = atom.x * cosY - atom.z * sinY;
      let z1 = atom.x * sinY + atom.z * cosY;
      let y2 = atom.y * cosX - z1 * sinX;
      let z2 = atom.y * sinX + z1 * cosX;
      return { ...atom, px: x1, py: y2, pz: z2 };
    });
    
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    projected.forEach(a => {
      if (a.px < minX) minX = a.px;
      if (a.px > maxX) maxX = a.px;
      if (a.py < minY) minY = a.py;
      if (a.py > maxY) maxY = a.py;
    });
    
    const rawScale = Math.min(width / (maxX - minX || 1), height / (maxY - minY || 1)) * 0.65;
    const finalScale = rawScale * zoom;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    const coords = projected.map(a => ({
      ...a,
      sx: cx + (a.px - centerX) * finalScale,
      sy: cy + (a.py - centerY) * finalScale
    }));
    
    coords.sort((a, b) => b.pz - a.pz);
    
    ctx.beginPath();
    ctx.lineWidth = 2 * zoom;
    ctx.strokeStyle = '#38bdf8';
    for (let i = 0; i < coords.length; i++) {
      for (let j = i + 1; j < Math.min(coords.length, i + 6); j++) {
        const dx = coords[i].x - coords[j].x;
        const dy = coords[i].y - coords[j].y;
        const dz = coords[i].z - coords[j].z;
        const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
        if (dist < 2.2) {
          ctx.moveTo(coords[i].sx, coords[i].sy);
          ctx.lineTo(coords[j].sx, coords[j].sy);
        }
      }
    }
    ctx.stroke();
    
    coords.forEach(a => {
      let radius = (a.isCa || a.isNucleic ? 5.5 : 4) * zoom;
      ctx.beginPath();
      ctx.arc(a.sx, a.sy, radius, 0, 2 * Math.PI);
      
      let color = '#38bdf8';
      if (a.element === 'C') color = '#94a3b8';
      else if (a.element === 'O') color = '#f43f5e';
      else if (a.element === 'N') color = '#3b82f6';
      else if (a.element === 'S') color = '#eab308';
      else if (a.element === 'P') color = '#a855f7';
      else if (a.element === 'H') color = '#f1f5f9';
      
      ctx.fillStyle = color;
      ctx.fill();
    });
  }, [atoms, rotation, zoom, safeFileName]);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      const deltaX = e.clientX - lastMousePos.current.x;
      const deltaY = e.clientY - lastMousePos.current.y;
      
      setRotation(prev => ({
        x: prev.x + deltaY * 0.01,
        y: prev.y + deltaX * 0.01
      }));
      
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseUp = () => { isDragging.current = false; };
  const handleWheel = (e) => {
    e.preventDefault();
    setZoom(prev => Math.max(0.3, Math.min(3.5, prev - e.deltaY * 0.001)));
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <canvas
        ref={canvasRef}
        width={750}
        height={520}
        style={{
          backgroundColor: '#0f172a',
          borderRadius: '12px',
          cursor: isDragging.current ? 'grabbing' : 'grab',
          border: '1px solid #334155',
          width: '100%',
          height: '100%',
          maxHeight: '540px'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      />
      <div style={{
        position: 'absolute',
        bottom: '16px',
        left: '16px',
        display: 'flex',
        gap: '8px',
        backgroundColor: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(8px)',
        padding: '8px 12px',
        borderRadius: '8px',
        border: '1px solid #334155'
      }}>
        <button
          onClick={() => setSpin(p => !p)}
          style={{
            backgroundColor: spin ? '#10b981' : '#475569',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '11px',
            cursor: 'pointer'
          }}
        >
          {spin ? '⏸️ Pause Spin' : '▶️ Auto Spin'}
        </button>
        <button
          onClick={() => { setRotation({ x: -0.5, y: 0.6 }); setZoom(1.0); }}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '11px',
            cursor: 'pointer'
          }}
        >
          🔄 Reset View
        </button>
      </div>
    </div>
  );
};

// ==========================================
// 3. Main MolStar MolViz Precision Engine
// ==========================================
const PDBViewer = ({ uniprotId }) => {
  // Unified Source-Of-Truth State
  const [structuralData, setStructuralData] = useState({
    content: null,
    fileName: '',
    format: 'pdb',
    source: '',
    rawId: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [structureInfo, setStructureInfo] = useState(null);
  const [activePanel, setActivePanel] = useState('representation');
  const [webglSupported] = useState(checkWebGLSupport());
  const [engineLoadFailed, setEngineLoadFailed] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  // Settings
  const [viewerSettings, setViewerSettings] = useState({
    visualStyle: 'cartoon',
    backgroundColor: '#0f172a',
    lighting: 'plastic',
    spin: false
  });

  const visualStyles = [
    { value: 'cartoon', label: 'Cartoon (Proteins)' },
    { value: 'ball-and-stick', label: 'Ball & Stick' },
    { value: 'spacefill', label: 'Spacefill (CPK)' },
    { value: 'surface', label: 'Molecular Surface' },
    { value: 'ribbon', label: 'Ribbon' }
  ];

  const backgroundColors = [
    { value: '#0f172a', label: 'Dark Obsidian', color: '#0f172a' },
    { value: '#000000', label: 'Pure Black', color: '#000000' },
    { value: '#1e293b', label: 'Slate Glass', color: '#1e293b' },
    { value: '#ffffff', label: 'Clean White', color: '#ffffff' },
    { value: '#f8fafc', label: 'Off-White', color: '#f8fafc' }
  ];

  const fileInputRef = useRef(null);
  const viewerContainerRef = useRef(null);
  const molstarContainerRef = useRef(null);
  const molstarViewerRef = useRef(null);
  const objectUrlRef = useRef(null);

  const sampleProteins = [
    { id: '1crn', name: 'Crambin (0.83Å)', category: 'Protein' },
    { id: '2ptc', name: 'Trypsin Inhibitor', category: 'Complex' },
    { id: '1bna', name: 'B-DNA Helix', category: 'Nucleic' },
    { id: '3j3q', name: 'SARS-CoV Spike', category: 'Viral' },
    { id: '1hsg', name: 'HIV Protease + Ligand', category: 'Drug Target' },
    { id: '4hhb', name: 'Hemoglobin Tetramer', category: 'Protein' },
    { id: '1ubq', name: 'Ubiquitin', category: 'Protein' },
    { id: '2gb1', name: 'Protein G B1', category: 'Protein' },
    { id: 'aspirin', name: 'Aspirin (Small Mol)', category: 'Small Mol', ext: 'sdf' },
    { id: 'caffeine', name: 'Caffeine (Small Mol)', category: 'Small Mol', ext: 'sdf' }
  ];

  // Format determination
  const determineFileFormat = (extension, content) => {
    const ext = (extension || '').toLowerCase();
    if (ext === 'sdf' || ext === 'mol' || (content && content.includes('$$$$'))) return 'sdf';
    if (ext === 'mol2' || (content && content.includes('@<TRIPOS>MOLECULE'))) return 'mol2';
    if (ext === 'cif' || ext === 'mmcif') return 'mmcif';
    return 'pdb';
  };

  // 1. Client & Server Hybrid Metadata & Dihedral Analysis
  const analyzeStructure = async (content, format = 'pdb') => {
    if (!content) return;

    // Try server-side analysis API first
    try {
      const api = apiConfig();
      if (api && typeof api.analyzeStructureApi === 'function') {
        const serverPayload = await api.analyzeStructureApi(content, format);
        if (serverPayload && serverPayload.metadata) {
          setStructureInfo(serverPayload.metadata);
          if (serverPayload.ramachandran) {
            setAnalysisResults({
              type: 'ramachandran',
              ...serverPayload.ramachandran
            });
          }
          return;
        }
      }
    } catch (serverErr) {
      console.warn('Server-side structure analysis fallback triggered:', serverErr);
    }

    // Client-side fallback parsing
    if (format === 'pdb' || format === 'mmcif') {
      analyzePDBStructure(content);
    } else if (format === 'sdf' || format === 'mol') {
      analyzeSDFStructure(content);
    }
  };

  const analyzePDBStructure = (pdbText) => {
    const lines = pdbText.split('\n');
    const info = {
      atoms: 0,
      residues: new Set(),
      chains: new Set(),
      hetAtoms: 0,
      waters: 0,
      title: '',
      resolution: '',
      method: '',
      ligands: new Set()
    };

    lines.forEach(line => {
      if (line.startsWith('ATOM')) {
        info.atoms++;
        info.chains.add(line.substring(21, 22).trim());
        info.residues.add(line.substring(17, 20).trim() + line.substring(22, 26).trim());
      } else if (line.startsWith('HETATM')) {
        info.hetAtoms++;
        const resName = line.substring(17, 20).trim();
        if (resName === 'HOH' || resName === 'WAT') {
          info.waters++;
        } else {
          info.ligands.add(resName);
        }
      } else if (line.startsWith('TITLE')) {
        info.title += line.substring(10).trim() + ' ';
      } else if (line.startsWith('REMARK   2 RESOLUTION')) {
        info.resolution = line.substring(23).trim() + ' Å';
      }
    });

    setStructureInfo({
      ...info,
      residues: info.residues.size,
      chains: info.chains.size,
      ligands: Array.from(info.ligands),
      title: info.title.trim()
    });
  };

  const analyzeSDFStructure = (sdfText) => {
    const lines = sdfText.split('\n');
    const info = { moleculeName: '', atoms: 0, bonds: 0 };
    if (lines.length >= 1) info.moleculeName = lines[0].trim();
    if (lines.length >= 4) {
      const countsLine = lines[3];
      if (countsLine.length >= 6) {
        info.atoms = parseInt(countsLine.substring(0, 3)) || 0;
        info.bonds = parseInt(countsLine.substring(3, 6)) || 0;
      }
    }
    setStructureInfo(info);
  };

  // 2. Render Structure to MolStar with Robust Timeout & Fallback
  const renderToMolStar = async (content, filename, format = 'pdb') => {
    if (!webglSupported) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      await loadMolstarAssets();

      if (!molstarContainerRef.current) return;

      // Revoke previous blob URL
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }

      let formatToUse = format.toLowerCase();
      if (formatToUse === 'mol' || formatToUse === 'sdf') formatToUse = 'sdf';
      else if (formatToUse === 'cif' || formatToUse === 'mmcif') formatToUse = 'mmcif';
      else formatToUse = 'pdb';

      const blob = new Blob([content], { type: 'text/plain' });
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;

      const hexColor = viewerSettings.backgroundColor || '#0f172a';
      const r = parseInt(hexColor.slice(1, 3), 16) || 15;
      const g = parseInt(hexColor.slice(3, 5), 16) || 23;
      const b = parseInt(hexColor.slice(5, 7), 16) || 42;

      const renderOptions = {
        customData: {
          url: objectUrl,
          format: formatToUse,
          binary: false
        },
        bgColor: { r, g, b },
        hideControls: false,
        hideCanvasControls: ['selection'],
        expanded: false,
        visualStyle: viewerSettings.visualStyle || 'cartoon',
        lighting: viewerSettings.lighting || 'plastic',
        highlightColor: { r: 56, g: 189, b: 248 },
        selectColor: { r: 16, g: 185, b: 129 }
      };

      if (!molstarViewerRef.current) {
        const viewer = new window.PDBeMolstarPlugin();
        molstarViewerRef.current = viewer;
        await viewer.render(molstarContainerRef.current, renderOptions);
      } else {
        try {
          await molstarViewerRef.current.clear();
        } catch (e) {}
        await molstarViewerRef.current.render(molstarContainerRef.current, renderOptions);
      }

      setEngineLoadFailed(false);
    } catch (err) {
      console.error('MolStar 3D Engine render error:', err);
      setError('3D MolStar render failed: ' + err.message);
      setEngineLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // 3. Unified State Synchronization Hook
  useEffect(() => {
    if (!structuralData.content) return;

    let isMounted = true;

    const synchronize = async () => {
      setLoading(true);
      setError('');

      // A. Synchronize metadata and analysis
      await analyzeStructure(structuralData.content, structuralData.format);

      // B. Synchronize 3D / 2D rendering
      if (isMounted) {
        if (webglSupported) {
          await renderToMolStar(structuralData.content, structuralData.fileName, structuralData.format);
        } else {
          setLoading(false);
        }
      }
    };

    synchronize();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuralData.content, structuralData.fileName, structuralData.format]);

  // 4. Dynamic Visual Settings Synchronization Hook
  useEffect(() => {
    const viewer = molstarViewerRef.current;
    if (!viewer || !webglSupported || engineLoadFailed || loading) return;

    // A. Update Background Color dynamically
    if (viewerSettings.backgroundColor) {
      try {
        const hexColor = viewerSettings.backgroundColor;
        const r = parseInt(hexColor.slice(1, 3), 16) || 15;
        const g = parseInt(hexColor.slice(3, 5), 16) || 23;
        const b = parseInt(hexColor.slice(5, 7), 16) || 42;
        
        if (viewer.canvas && typeof viewer.canvas.setBgColor === 'function') {
          viewer.canvas.setBgColor({ r, g, b });
        } else if (viewer.visual && typeof viewer.visual.update === 'function') {
          viewer.visual.update({ bgColor: { r, g, b } });
        }
      } catch (err) {
        console.warn('Error dynamically updating MolStar background color:', err);
      }
    }

    // B. Update Representation / Visual Style and Lighting dynamically
    try {
      if (viewer.visual && typeof viewer.visual.update === 'function') {
        viewer.visual.update({
          visualStyle: viewerSettings.visualStyle,
          lighting: viewerSettings.lighting
        });
      }
    } catch (err) {
      console.warn('Error dynamically updating MolStar visual settings:', err);
    }
    
    // C. Update Spin State dynamically
    try {
      if (viewer.visual && typeof viewer.visual.toggleSpin === 'function') {
        viewer.visual.toggleSpin(viewerSettings.spin);
      }
    } catch (err) {
      console.warn('Error dynamically toggling MolStar spin:', err);
    }
  }, [viewerSettings.visualStyle, viewerSettings.lighting, viewerSettings.backgroundColor, viewerSettings.spin, webglSupported, engineLoadFailed, loading]);

  // Load initial structure on mount
  useEffect(() => {
    loadSampleProtein(uniprotId || '1crn');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    setError('');

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const extension = file.name.split('.').pop();
        const format = determineFileFormat(extension, content);

        // DISPATCH TO UNIFIED STATE
        setStructuralData({
          content,
          fileName: file.name,
          format,
          source: 'user_upload',
          rawId: file.name
        });
      };
      reader.onerror = () => {
        setError('Failed to read uploaded file');
        setLoading(false);
      };
      reader.readAsText(file);
    } catch (err) {
      setError('Error processing file: ' + err.message);
      setLoading(false);
    }
  };

  const loadSampleProtein = async (proteinId) => {
    setLoading(true);
    setError('');
    const sampleObj = sampleProteins.find(s => s.id.toLowerCase() === proteinId.toLowerCase());
    const ext = (sampleObj && sampleObj.ext) ? sampleObj.ext : 'pdb';

    try {
      let data = null;
      let source = 'local_cache';

      // 1. Try local static cache
      try {
        const localResponse = await fetch(`${process.env.PUBLIC_URL || ''}/pdb/${proteinId.toLowerCase()}.${ext}`);
        if (localResponse.ok) {
          const text = await localResponse.text();
          if (text && !text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            data = text;
          }
        }
      } catch (localErr) {
        console.warn('Local static fetch failed, trying network fallback...', localErr);
      }

      // 2. RCSB or PubChem fallback
      if (!data) {
        source = 'network_api';
        if (ext === 'sdf') {
          const sdfUrl = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${proteinId}/SDF`;
          const res = await fetch(sdfUrl);
          if (res.ok) data = await res.text();
        } else {
          const rcsbUrl = `https://files.rcsb.org/download/${proteinId.toUpperCase()}.pdb`;
          const res = await fetch(rcsbUrl);
          if (res.ok) data = await res.text();
        }
      }

      // 3. AlphaFold DB fallback
      if (!data && ext === 'pdb') {
        source = 'alphafold_api';
        try {
          const afUrl = `https://alphafold.ebi.ac.uk/files/AF-${proteinId.toUpperCase()}-F1-model_v4.pdb`;
          const afRes = await fetch(afUrl);
          if (afRes.ok) data = await afRes.text();
        } catch (afErr) {}
      }

      if (data && !data.startsWith('<!DOCTYPE') && !data.startsWith('<html')) {
        const fname = `${proteinId}.${ext}`;
        const format = determineFileFormat(ext, data);

        // DISPATCH TO UNIFIED STATE
        setStructuralData({
          content: data,
          fileName: fname,
          format,
          source,
          rawId: proteinId
        });
      } else {
        setError(`Failed to load structure (${proteinId}): structure unavailable.`);
        setLoading(false);
      }
    } catch (err) {
      setError('Error fetching structure: ' + err.message);
      setLoading(false);
    }
  };

  const focusBindingSite = () => {
    if (molstarViewerRef.current) {
      try {
        molstarViewerRef.current.visual.focus();
      } catch (err) {
        console.warn('MolStar focus error:', err);
      }
    }
  };

  const handleSettingChange = (setting, value) => {
    setViewerSettings(prev => ({ ...prev, [setting]: value }));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      viewerContainerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const resetView = () => {
    const viewer = molstarViewerRef.current;
    if (viewer && webglSupported && !engineLoadFailed) {
      try {
        if (viewer.visual && typeof viewer.visual.reset === 'function') {
          viewer.visual.reset({ camera: true, theme: true });
          return;
        }
      } catch (err) {
        console.warn('Dynamic visual reset failed:', err);
      }
    }
    if (structuralData.content) {
      renderToMolStar(structuralData.content, structuralData.fileName, structuralData.format);
    }
  };

  const handleSelectResidue = (chain, resNo, color = { r: 239, g: 68, b: 68 }) => {
    const viewer = molstarViewerRef.current;
    if (!viewer || !webglSupported || engineLoadFailed) return;

    try {
      if (viewer.visual && typeof viewer.visual.select === 'function') {
        viewer.visual.select({
          data: [{
            struct_asym_id: chain,
            start_residue_number: resNo,
            end_residue_number: resNo,
            color: color,
            focus: true
          }]
        });
      }
    } catch (err) {
      console.warn('Error focusing residue in MolStar:', err);
    }
  };

  const handleSelectHBondPair = (pair) => {
    const viewer = molstarViewerRef.current;
    if (!viewer || !webglSupported || engineLoadFailed) return;

    try {
      if (viewer.visual && typeof viewer.visual.select === 'function') {
        viewer.visual.select({
          data: [
            {
              struct_asym_id: pair.donorChain,
              start_residue_number: pair.donorResNo,
              end_residue_number: pair.donorResNo,
              color: { r: 56, g: 189, b: 248 }, // blue
              focus: true
            },
            {
              struct_asym_id: pair.acceptorChain,
              start_residue_number: pair.acceptorResNo,
              end_residue_number: pair.acceptorResNo,
              color: { r: 244, g: 63, b: 94 } // rose
            }
          ]
        });
      }
    } catch (err) {
      console.warn('Error focusing H-Bond pair in MolStar:', err);
    }
  };

  // Dihedral Ramachandran Analysis
  const performRamachandranAnalysis = () => {
    if (!structuralData.content) return;

    const calculateDihedralIUPAC = (p1, p2, p3, p4) => {
      const u = { x: p2.x - p1.x, y: p2.y - p1.y, z: p2.z - p1.z };
      const v = { x: p3.x - p2.x, y: p3.y - p2.y, z: p3.z - p2.z };
      const w = { x: p4.x - p3.x, y: p4.y - p3.y, z: p4.z - p3.z };

      const cross = (a, b) => ({
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x
      });

      const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
      const norm = (a) => Math.sqrt(dot(a, a)) || 1;

      const n1 = cross(u, v);
      const n2 = cross(v, w);
      const vNorm = norm(v);
      const vUnit = { x: v.x / vNorm, y: v.y / vNorm, z: v.z / vNorm };

      const x = dot(n1, n2);
      const y = dot(cross(n1, n2), vUnit);
      return Math.atan2(y, x) * (180 / Math.PI);
    };

    const lines = structuralData.content.split('\n');
    const residuesByChain = {};

    lines.forEach(line => {
      if (line.startsWith('ATOM')) {
        const atomName = line.substring(12, 16).trim();
        if (['N', 'CA', 'C'].includes(atomName)) {
          const chain = line.substring(21, 22).trim() || 'A';
          const resNo = parseInt(line.substring(22, 26).trim());
          const resName = line.substring(17, 20).trim();
          const x = parseFloat(line.substring(30, 38));
          const y = parseFloat(line.substring(38, 46));
          const z = parseFloat(line.substring(46, 54));

          if (!isNaN(resNo) && !isNaN(x) && !isNaN(y) && !isNaN(z)) {
            if (!residuesByChain[chain]) residuesByChain[chain] = {};
            if (!residuesByChain[chain][resNo]) residuesByChain[chain][resNo] = { resNo, resName };
            residuesByChain[chain][resNo][atomName] = { x, y, z };
          }
        }
      }
    });

    let totalEvaluated = 0;
    let alphaCount = 0;
    let betaCount = 0;
    let favoredCount = 0;
    let outliers = [];

    Object.keys(residuesByChain).forEach(chain => {
      const resNos = Object.keys(residuesByChain[chain]).map(Number).sort((a,b) => a - b);
      const resList = resNos.map(rNo => residuesByChain[chain][rNo]);

      for (let i = 1; i < resList.length - 1; i++) {
        const prevRes = resList[i - 1];
        const res = resList[i];
        const nextRes = resList[i + 1];

        if (!res.N || !res.CA || !res.C) continue;
        if (!prevRes.C || !nextRes.N) continue;

        const phi = calculateDihedralIUPAC(prevRes.C, res.N, res.CA, res.C);
        const psi = calculateDihedralIUPAC(res.N, res.CA, res.C, nextRes.N);

        totalEvaluated++;
        const isAlpha = (phi >= -140 && phi <= -30 && psi >= -90 && psi <= 20);
        const isBeta = (phi >= -180 && phi <= -40 && ((psi >= 40 && psi <= 180) || (psi >= -180 && psi <= -140)));
        
        if (isAlpha) alphaCount++;
        if (isBeta) betaCount++;

        if (isAlpha || isBeta || res.resName === 'GLY') {
          favoredCount++;
        } else {
          outliers.push({ resName: res.resName, resNo: res.resNo, chain, phi: phi.toFixed(1), psi: psi.toFixed(1) });
        }
      }
    });

    setAnalysisResults({
      type: 'ramachandran',
      totalResidues: totalEvaluated,
      alphaCount,
      betaCount,
      favoredPercent: totalEvaluated > 0 ? (favoredCount / totalEvaluated) * 100 : 0,
      outliers: outliers.slice(0, 10)
    });
  };

  // Real Hydrogen Bond Analyzer
  const performHydrogenBondAnalysis = () => {
    if (!structuralData.content) return;

    const lines = structuralData.content.split('\n');
    const donors = [];
    const acceptors = [];

    lines.forEach(line => {
      if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
        const atomName = line.substring(12, 16).trim();
        const resNo = parseInt(line.substring(22, 26).trim());
        const resName = line.substring(17, 20).trim();
        const chain = line.substring(21, 22).trim() || 'A';
        const x = parseFloat(line.substring(30, 38));
        const y = parseFloat(line.substring(38, 46));
        const z = parseFloat(line.substring(46, 54));

        if (atomName === 'N' || atomName.startsWith('NE') || atomName.startsWith('NH')) {
          donors.push({ atomName, resNo, resName, chain, x, y, z, label: `${resName}${resNo}:${atomName}` });
        } else if (atomName === 'O' || atomName.startsWith('OD') || atomName.startsWith('OE')) {
          acceptors.push({ atomName, resNo, resName, chain, x, y, z, label: `${resName}${resNo}:${atomName}` });
        }
      }
    });

    const hbonds = [];
    let sumDist = 0;

    donors.forEach(d => {
      acceptors.forEach(a => {
        if (d.chain === a.chain && Math.abs(d.resNo - a.resNo) <= 2) return;

        const dx = d.x - a.x;
        const dy = d.y - a.y;
        const dz = d.z - a.z;
        const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);

        if (dist >= 2.6 && dist <= 3.4) {
          hbonds.push({
            donor: d.label,
            donorResNo: d.resNo,
            donorChain: d.chain,
            acceptor: a.label,
            acceptorResNo: a.resNo,
            acceptorChain: a.chain,
            dist: dist.toFixed(2)
          });
          sumDist += dist;
        }
      });
    });

    setAnalysisResults({
      type: 'hydrogen_bonds',
      estimatedBonds: hbonds.length,
      averageDistance: hbonds.length > 0 ? (sumDist / hbonds.length).toFixed(2) : 0,
      samplePairs: hbonds.slice(0, 6)
    });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Georgia, serif',
      color: '#f8fafc',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: 'rgba(15, 23, 42, 0.85)',
      backdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      color: 'white',
      padding: '20px 28px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    headerTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      fontFamily: 'Georgia, serif',
      margin: 0,
      letterSpacing: '-0.02em',
      background: 'linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    headerSubtitle: {
      color: '#94a3b8',
      fontSize: '0.9rem',
      marginTop: '4px'
    },
    mainContent: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden'
    },
    leftPanel: {
      width: '360px',
      backgroundColor: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(12px)',
      boxShadow: '4px 0 20px rgba(0, 0, 0, 0.25)',
      overflowY: 'auto',
      borderRight: '1px solid rgba(255, 255, 255, 0.1)'
    },
    section: {
      padding: '20px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#38bdf8'
    },
    uploadButton: {
      width: '100%',
      background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '10px 16px',
      borderRadius: '8px',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '12px',
      fontSize: '13px',
      transition: 'all 0.2s',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
    },
    sampleGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginTop: '8px'
    },
    sampleButton: {
      padding: '8px 10px',
      fontSize: '11px',
      fontWeight: '500',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '6px',
      backgroundColor: 'rgba(30, 41, 59, 0.8)',
      color: '#e2e8f0',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.2s'
    },
    structureInfo: {
      backgroundColor: 'rgba(30, 41, 59, 0.6)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '8px',
      padding: '14px'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '10px',
      fontSize: '13px'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column'
    },
    infoLabel: {
      color: '#94a3b8',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    infoValue: {
      fontWeight: '700',
      color: '#38bdf8',
      fontSize: '15px'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      marginBottom: '16px'
    },
    tab: {
      flex: 1,
      padding: '8px 4px',
      fontSize: '12px',
      fontWeight: '600',
      border: 'none',
      backgroundColor: 'transparent',
      color: '#94a3b8',
      cursor: 'pointer',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s',
      textAlign: 'center'
    },
    activeTab: {
      borderBottomColor: '#38bdf8',
      color: '#38bdf8'
    },
    controlGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: '600',
      color: '#cbd5e1',
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      borderRadius: '6px',
      fontSize: '13px',
      backgroundColor: '#1e293b',
      color: '#f8fafc',
      outline: 'none'
    },
    rightPanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#0f172a'
    },
    viewerControls: {
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '12px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    controlButton: {
      padding: '8px 14px',
      backgroundColor: 'rgba(30, 41, 59, 0.8)',
      color: '#e2e8f0',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      marginRight: '8px',
      transition: 'all 0.2s'
    },
    viewerContainer: {
      flex: 1,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    },
    molstarContainer: {
      width: '100%',
      height: '100%',
      minHeight: '520px',
      position: 'relative'
    },
    errorBox: {
      marginTop: '12px',
      padding: '10px 14px',
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      color: '#fca5a5',
      borderRadius: '6px',
      fontSize: '12px'
    },
    fileNameTag: {
      display: 'inline-block',
      marginTop: '10px',
      padding: '4px 10px',
      backgroundColor: 'rgba(16, 185, 129, 0.15)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      color: '#34d399',
      fontSize: '11px',
      borderRadius: '999px'
    },
    toolButton: {
      padding: '6px 10px',
      fontSize: '11px',
      fontWeight: '600',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '5px',
      backgroundColor: 'rgba(30, 41, 59, 0.8)',
      color: '#cbd5e1',
      cursor: 'pointer',
      marginRight: '6px',
      marginBottom: '6px',
      transition: 'all 0.2s'
    },
    analysisCard: {
      backgroundColor: 'rgba(30, 41, 59, 0.7)',
      border: '1px solid rgba(56, 189, 248, 0.2)',
      borderRadius: '8px',
      padding: '12px',
      marginTop: '12px',
      fontSize: '12px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Top Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>🧬 Mol* (MolStar) Precision Engine</h1>
          <p style={styles.headerSubtitle}>
            Next-generation 3D molecular visualization, docking diagnostics, and structural analytics
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', padding: '4px 10px', backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', borderRadius: '12px', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
            Mol* WebGL Engine
          </span>
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Left Side Control Panel */}
        <div style={styles.leftPanel}>

          {/* File Upload Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              📁 Load Structure
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdb,.mol,.mol2,.sdf,.cif,.mmcif,.xyz"
              style={{ display: 'none' }}
            />
            <button
              style={styles.uploadButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              {loading ? '⏳ Processing...' : '📄 Upload Structure File'}
            </button>
            <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
              PDB, MOL, MOL2, SDF, CIF, mmCIF, XYZ
            </div>

            {structuralData.fileName && (
              <div style={styles.fileNameTag}>
                ✓ Loaded: {structuralData.fileName} {structuralData.source ? `(${structuralData.source})` : ''}
              </div>
            )}

            {error && (
              <div style={styles.errorBox}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
                Sample Structures & Standards:
              </p>
              <div style={styles.sampleGrid}>
                {sampleProteins.map(protein => (
                  <button
                    key={protein.id}
                    style={styles.sampleButton}
                    onClick={() => loadSampleProtein(protein.id)}
                    disabled={loading}
                  >
                    <div style={{ fontWeight: '600' }}>{protein.name}</div>
                    <div style={{ fontSize: '9px', color: '#64748b' }}>{protein.category}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Structural Metrics */}
          {structureInfo && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                📊 Structural Metrics
              </div>
              <div style={styles.structureInfo}>
                {structureInfo.title && (
                  <div style={{ marginBottom: '10px', fontWeight: '600', fontSize: '12px', color: '#f1f5f9' }}>
                    {structureInfo.title}
                  </div>
                )}
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Atoms</div>
                    <div style={styles.infoValue}>{structureInfo.atoms || 0}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Residues</div>
                    <div style={styles.infoValue}>{structureInfo.residues || 0}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Chains</div>
                    <div style={styles.infoValue}>{structureInfo.chains || 0}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Het-Atoms</div>
                    <div style={styles.infoValue}>{structureInfo.hetAtoms || 0}</div>
                  </div>
                  {structureInfo.waters !== undefined && (
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Waters</div>
                      <div style={styles.infoValue}>{structureInfo.waters}</div>
                    </div>
                  )}
                  {structureInfo.resolution && (
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Resolution</div>
                      <div style={styles.infoValue}>{structureInfo.resolution}</div>
                    </div>
                  )}
                </div>

                {structureInfo.ligands && structureInfo.ligands.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={styles.infoLabel}>Ligands</div>
                    <div style={{ fontSize: '11px', color: '#38bdf8', fontWeight: '500' }}>
                      {structureInfo.ligands.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Interactive Tools Panel */}
          <div style={styles.section}>
            <div style={styles.tabs}>
              {[
                { id: 'representation', label: 'Display' },
                { id: 'analysis', label: 'Analysis' }
              ].map(tab => (
                <button
                  key={tab.id}
                  style={{
                    ...styles.tab,
                    ...(activePanel === tab.id ? styles.activeTab : {})
                  }}
                  onClick={() => setActivePanel(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Display Tab */}
            {activePanel === 'representation' && (
              <div>
                <div style={styles.controlGroup}>
                  <label style={styles.label}>Visual Model</label>
                  <select
                    style={styles.select}
                    value={viewerSettings.visualStyle}
                    onChange={(e) => handleSettingChange('visualStyle', e.target.value)}
                  >
                    {visualStyles.map(rep => (
                      <option key={rep.value} value={rep.value}>{rep.label}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.controlGroup}>
                  <label style={styles.label}>Lighting Mode</label>
                  <select
                    style={styles.select}
                    value={viewerSettings.lighting}
                    onChange={(e) => handleSettingChange('lighting', e.target.value)}
                  >
                    <option value="plastic">Plastic Smooth</option>
                    <option value="flat">Flat Diffuse</option>
                    <option value="metallic">Metallic High-Gloss</option>
                  </select>
                </div>

                <div style={styles.controlGroup}>
                  <label style={styles.label}>Canvas Background</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px' }}>
                    {backgroundColors.map(bg => (
                      <button
                        key={bg.value}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid ' + (viewerSettings.backgroundColor === bg.color ? '#38bdf8' : 'rgba(255, 255, 255, 0.12)'),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '11px',
                          backgroundColor: 'rgba(30, 41, 59, 0.8)',
                          color: '#e2e8f0',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleSettingChange('backgroundColor', bg.color)}
                      >
                        <div style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: bg.color, border: '1px solid rgba(255,255,255,0.2)' }} />
                        {bg.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={styles.controlGroup}>
                  <label style={styles.label}>3D Auto-Rotate (Spin)</label>
                  <button
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid ' + (viewerSettings.spin ? '#10b981' : 'rgba(255, 255, 255, 0.15)'),
                      backgroundColor: viewerSettings.spin ? 'rgba(16, 185, 129, 0.15)' : 'rgba(30, 41, 59, 0.8)',
                      color: viewerSettings.spin ? '#10b981' : '#cbd5e1',
                      fontWeight: '600',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleSettingChange('spin', !viewerSettings.spin)}
                  >
                    {viewerSettings.spin ? '⏸️ Pause 3D Spin' : '▶️ Play 3D Spin'}
                  </button>
                </div>
              </div>
            )}

            {/* Analysis Tab */}
            {activePanel === 'analysis' && (
              <div>
                <div style={styles.controlGroup}>
                  <label style={styles.label}>Macromolecular Analytics</label>
                  <div>
                    <button style={styles.toolButton} onClick={performRamachandranAnalysis} disabled={!structuralData.content}>
                      📊 Ramachandran Plot
                    </button>
                    <button style={styles.toolButton} onClick={performHydrogenBondAnalysis} disabled={!structuralData.content}>
                      🔗 H-Bond Network
                    </button>
                  </div>
                </div>

                {analysisResults && (
                  <div style={styles.analysisCard}>
                    {analysisResults.type === 'ramachandran' && (
                      <div>
                        <div style={{ fontWeight: '700', color: '#38bdf8', marginBottom: '6px' }}>
                          Ramachandran Plot Distribution
                        </div>
                        <div>Evaluated Residues: {analysisResults.totalResidues}</div>
                        <div>Alpha-Helix Region: {analysisResults.alphaCount}</div>
                        <div>Beta-Sheet Region: {analysisResults.betaCount}</div>
                        <div style={{ marginTop: '4px', fontWeight: '700', color: '#10b981' }}>
                          Favored Region: {analysisResults.favoredPercent.toFixed(1)}%
                        </div>
                        {analysisResults.outliers && analysisResults.outliers.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ color: '#f87171', fontWeight: '600', marginBottom: '4px' }}>Outlier Residues (Click to Focus):</div>
                            <div style={{ fontSize: '10px', color: '#cbd5e1', maxHeight: '120px', overflowY: 'auto' }}>
                              {analysisResults.outliers.map((o, i) => (
                                <div
                                  key={i}
                                  onClick={() => handleSelectResidue(o.chain, parseInt(o.resNo), { r: 239, g: 68, b: 68 })}
                                  style={{
                                    padding: '4px 6px',
                                    margin: '2px 0',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                    border: '1px solid rgba(239, 68, 68, 0.15)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)'; }}
                                >
                                  <span>📍 {o.resName}{o.resNo} (Chain {o.chain})</span>
                                  <span style={{ color: '#f87171' }}>φ={o.phi}°, ψ={o.psi}°</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {analysisResults.type === 'hydrogen_bonds' && (
                      <div>
                        <div style={{ fontWeight: '700', color: '#38bdf8', marginBottom: '6px' }}>
                          Hydrogen Bond Network Summary
                        </div>
                        <div>Total H-Bonds Detected: {analysisResults.estimatedBonds}</div>
                        <div>Avg Donor-Acceptor Distance: {analysisResults.averageDistance} Å</div>
                        {analysisResults.samplePairs && analysisResults.samplePairs.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ color: '#38bdf8', fontWeight: '600', marginBottom: '4px' }}>Sample Contact Pairs (Click to Highlight):</div>
                            <div style={{ fontSize: '10px', color: '#cbd5e1', maxHeight: '120px', overflowY: 'auto' }}>
                              {analysisResults.samplePairs.map((pair, i) => (
                                <div
                                  key={i}
                                  onClick={() => handleSelectHBondPair(pair)}
                                  style={{
                                    padding: '6px',
                                    margin: '3px 0',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    backgroundColor: 'rgba(56, 189, 248, 0.08)',
                                    border: '1px solid rgba(56, 189, 248, 0.15)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(56, 189, 248, 0.2)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(56, 189, 248, 0.08)'; }}
                                >
                                  <span>🔗 {pair.donor} ↔ {pair.acceptor}</span>
                                  <span style={{ color: '#38bdf8', fontWeight: 'bold' }}>{pair.dist} Å</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Viewport Container */}
        <div style={styles.rightPanel}>
          <div style={styles.viewerControls}>
            <div>
              <button style={styles.controlButton} onClick={resetView}>
                🔄 Reset View
              </button>
              <button style={styles.controlButton} onClick={focusBindingSite}>
                🎯 Focus Binding Site
              </button>
              <button style={styles.controlButton} onClick={toggleFullscreen}>
                {isFullscreen ? '⛶ Exit Fullscreen' : '⛶ Fullscreen'}
              </button>
            </div>
          </div>

          <div style={styles.viewerContainer} ref={viewerContainerRef}>
            {(!webglSupported || engineLoadFailed) && structuralData.content && !loading ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '12px' }}>
                  {!webglSupported
                    ? '⚠️ WebGL 3D context unavailable in your browser. Showing High-Fidelity 2D Interactive Fallback.'
                    : `⚠️ 3D Mol* Engine failed to initialize (${error || 'Asset load failure'}). Showing 2D Interactive Fallback.`
                  }
                </div>
                <Molecule2DViewer data={structuralData.content} fileName={structuralData.fileName} />
              </div>
            ) : (
              <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '520px' }}>
                {/* Pure unmanaged container for MolStar */}
                <div ref={molstarContainerRef} style={styles.molstarContainer} />

                {/* Empty State Overlay */}
                {!structuralData.content && !loading && (
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#94a3b8', padding: '40px' }}>
                    <div style={{ fontSize: '3.5rem', opacity: 0.4, marginBottom: '12px' }}>🧬</div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#f8fafc', fontFamily: 'Georgia, serif' }}>
                      No Molecule Loaded
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px' }}>
                      Select a sample protein from the left menu or upload a local structure file (.pdb, .sdf, .mol, .cif) to initiate 3D Mol* rendering.
                    </p>
                  </div>
                )}

                {/* Loading State Overlay */}
                {loading && (
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#38bdf8', padding: '40px', backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', zIndex: 10 }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>⏳</div>
                    <p style={{ fontSize: '13px' }}>Initializing Mol* Engine & rendering 3D structure...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDBViewer;
